import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Car, Settings, Zap, Image, Thermometer, Wrench, MapPin, 
  Battery, Cpu, Shield, ChevronRight, CheckCircle, AlertCircle,
  Edit, Save, X, Clock, Wrench as Tool, AlertTriangle, TrendingUp,
  Activity, Droplets, Cable, Package, Circle, Gauge, FileWarning, Search
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * VehicleDetailView - Detaillierte Fahrzeugansicht für Admin und Endbenutzer
 * Zeigt alle gesammelten Daten in übersichtlicher Form
 * 
 * @param {Object} vehicle - Fahrzeugdaten
 * @param {Function} onClose - Callback zum Schließen
 * @param {Function} onSave - Callback zum Speichern (nur Admin)
 * @param {boolean} isAdmin - Ist der Benutzer Admin?
 * @param {Object} userFeatures - Features aus dem Abo-Plan des Benutzers (für Gating)
 */
const VehicleDetailView = ({ vehicle, onClose, onSave, isAdmin = true, userFeatures = {}, availableVariants = [], showHeader = true }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(vehicle);
  const [selectedVariant, setSelectedVariant] = useState(vehicle?.variant || '');
  const [loadingData, setLoadingData] = useState(false);
  const [vehicleData, setVehicleData] = useState(vehicle);
  const [repairVideos, setRepairVideos] = useState([]);
  const [dataStatus, setDataStatus] = useState(null);

  // Auto-load complete data on mount
  useEffect(() => {
    if (vehicle?.id) {
      loadCompleteData();
    }
  }, [vehicle?.id]);

  const loadCompleteData = async () => {
    if (!vehicle?.id) return;
    
    setLoadingData(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/vehicles/master/${vehicle.id}/complete-data?include_videos=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.vehicle) {
          setVehicleData({ ...vehicle, ...data.vehicle });
          setEditData({ ...vehicle, ...data.vehicle });
        }
        if (data.repair_videos) {
          setRepairVideos(data.repair_videos);
        }
        if (data.data_status) {
          setDataStatus(data.data_status);
        }
      }
    } catch (error) {
      console.error('Error loading complete vehicle data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  if (!vehicle) return null;

  // Use loaded data or fallback to prop
  const displayVehicle = vehicleData || vehicle;

  // Feature-Gating: Prüfen ob Benutzer Zugriff auf bestimmte Features hat
  const hasRepairAccess = isAdmin || userFeatures.repair_instructions === true;
  const hasTuningAccess = isAdmin || userFeatures.tuning_data === true;
  const hasFullAccess = isAdmin || userFeatures.technical_specifications === true;

  const handleSave = () => {
    if (onSave) {
      onSave(editData);
    }
    setEditMode(false);
  };

  // Data availability check
  const hasData = (field) => {
    const data = displayVehicle[field];
    return data && (Array.isArray(data) ? data.length > 0 : Object.keys(data).length > 0);
  };

  // If showHeader is false, render without the fixed modal wrapper
  if (!showHeader) {
    return (
      <div className="flex flex-col h-full">
        {/* Loading Indicator */}
        {loadingData && (
          <div className="px-4 py-2 bg-[#1EA0E6]/10 border-b border-[#1EA0E6]/30 flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#1EA0E6] border-t-transparent" />
            <span className="text-sm text-[#1EA0E6]">Lade Fahrzeugdaten...</span>
          </div>
        )}
        
        {/* Completeness Bar */}
        <div className="px-4 py-2 bg-white/5">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400">Vollständigkeit:</span>
            <div className="flex-1 bg-gray-700 rounded-full h-2">
              <div 
                className="h-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500"
                style={{ width: `${displayVehicle.completeness || 0}%` }}
              />
            </div>
            <span className="text-sm font-medium text-white">{displayVehicle.completeness || 0}%</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={loadCompleteData}
              disabled={loadingData}
              className="text-xs text-gray-400 hover:text-white"
            >
              Aktualisieren
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-4 py-2 bg-white/5 border-b border-white/10">
            <TabsList className="flex flex-wrap gap-1 h-auto bg-transparent">
              <TabsTrigger value="overview" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">Übersicht</TabsTrigger>
              <TabsTrigger value="specs" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Spezifikationen {hasData('specifications') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              {hasTuningAccess && (
                <TabsTrigger value="tuning" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                  Tuning {hasData('tuning') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
                </TabsTrigger>
              )}
              <TabsTrigger value="diagnostics" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Diagnose {(hasData('advanced_data.dtc_library') || vehicle.advanced_data?.dtc_library) && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="service" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Service {(hasData('advanced_data.service_intervals') || vehicle.advanced_data?.service_intervals) && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="electrical" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Elektrik {(hasData('advanced_data.network_topology') || vehicle.advanced_data?.network_topology) && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="parts" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Teilenummern {(hasData('advanced_data.part_numbers') || vehicle.advanced_data?.part_numbers) && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="wheels" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Räder/TPMS {(hasData('advanced_data.wheel_tire_fitment') || vehicle.advanced_data?.wheel_tire_fitment) && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="failures" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Schwachstellen {(hasData('advanced_data.failure_modes') || vehicle.advanced_data?.failure_modes) && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="ac" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Klimaanlage {hasData('air_conditioning') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="torque" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Drehmomente {hasData('torque_specs') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              {hasRepairAccess && (
                <TabsTrigger value="repairs" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                  Reparaturen {hasData('repair_guides') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
                </TabsTrigger>
              )}
              <TabsTrigger value="locations" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Positionen {hasData('component_locations') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="fuses" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Sicherungen {hasData('fuses_relays') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="ecus" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Steuergeräte {hasData('ecu_locations') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="battery" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Batterie {hasData('battery') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <TabsContent value="overview" className="mt-0">
              <OverviewTab vehicle={vehicle} onTabChange={setActiveTab} />
            </TabsContent>
            <TabsContent value="specs" className="mt-0">
              <SpecsTab specs={displayVehicle.specifications} />
            </TabsContent>
            {hasTuningAccess && (
              <TabsContent value="tuning" className="mt-0">
                <TuningTab tuning={displayVehicle.tuning} />
              </TabsContent>
            )}
            <TabsContent value="diagnostics" className="mt-0">
              <DiagnosticsTab data={displayVehicle.advanced_data} />
            </TabsContent>
            <TabsContent value="service" className="mt-0">
              <ServiceTab data={displayVehicle.advanced_data} />
            </TabsContent>
            <TabsContent value="electrical" className="mt-0">
              <ElectricalTab data={displayVehicle.advanced_data} />
            </TabsContent>
            <TabsContent value="parts" className="mt-0">
              <PartsTab data={displayVehicle.advanced_data} />
            </TabsContent>
            <TabsContent value="wheels" className="mt-0">
              <WheelsTab data={displayVehicle.advanced_data} />
            </TabsContent>
            <TabsContent value="failures" className="mt-0">
              <FailuresTab data={displayVehicle.advanced_data} />
            </TabsContent>
            <TabsContent value="ac" className="mt-0">
              <ACTab data={displayVehicle.air_conditioning} />
            </TabsContent>
            <TabsContent value="torque" className="mt-0">
              <TorqueTab specs={displayVehicle.torque_specs} />
            </TabsContent>
            {hasRepairAccess && (
              <TabsContent value="repairs" className="mt-0">
                <RepairsTab guides={displayVehicle.repair_guides} vehicleId={displayVehicle.id} brand={displayVehicle.brand} model={displayVehicle.model} videos={repairVideos} />
              </TabsContent>
            )}
            <TabsContent value="locations" className="mt-0">
              <LocationsTab locations={displayVehicle.component_locations} />
            </TabsContent>
            <TabsContent value="fuses" className="mt-0">
              <FusesTab fuses={displayVehicle.fuses_relays} />
            </TabsContent>
            <TabsContent value="ecus" className="mt-0">
              <ECUsTab ecus={displayVehicle.ecu_locations} />
            </TabsContent>
            <TabsContent value="battery" className="mt-0">
              <BatteryTab battery={displayVehicle.battery} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border border-white/20 rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-4">
            {vehicle.image?.path && (
              <img
                src={`${BACKEND_URL}/api${vehicle.image.path}`}
                alt=""
                className="w-16 h-16 rounded-lg object-cover bg-white/5"
                onError={(e) => {
                  // Hide broken image - file missing on server
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <div>
              <h2 className="text-xl font-bold text-white">
                {displayVehicle.brand} {displayVehicle.model}
              </h2>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {/* Typenbezeichnung / Variante */}
                {displayVehicle.variant && (
                  <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">
                    {displayVehicle.variant}
                  </span>
                )}
                {/* Baujahr */}
                {displayVehicle.year_start && (
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                    {displayVehicle.year_start}{displayVehicle.year_end ? ` - ${displayVehicle.year_end}` : '+'}
                  </span>
                )}
                {/* Kraftstoff */}
                {displayVehicle.fuel_type && (
                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                    {displayVehicle.fuel_type}
                  </span>
                )}
                {/* Motor-Code wenn vorhanden */}
                {displayVehicle.engine_code && (
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full font-mono">
                    {displayVehicle.engine_code}
                  </span>
                )}
              </div>
              
              {/* Motor-Varianten Dropdown */}
              {availableVariants && availableVariants.length > 1 && (
                <div className="mt-2">
                  <select
                    value={selectedVariant}
                    onChange={(e) => setSelectedVariant(e.target.value)}
                    className="text-xs bg-white/10 border border-white/20 rounded px-2 py-1 text-white"
                  >
                    <option value="">Motor wählen...</option>
                    {availableVariants.map((v, i) => (
                      <option key={i} value={v.variant || v.name}>
                        {v.variant || v.name} {v.power_ps ? `(${v.power_ps} PS)` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant={editMode ? "default" : "outline"}
                size="sm"
                onClick={() => editMode ? handleSave() : setEditMode(true)}
                className={editMode ? "bg-green-600" : "border-white/20 text-white"}
              >
                {editMode ? <Save className="w-4 h-4 mr-1" /> : <Edit className="w-4 h-4 mr-1" />}
                {editMode ? 'Speichern' : 'Bearbeiten'}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-400">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Completeness Bar */}
        <div className="px-4 py-2 bg-white/5">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400">Vollständigkeit:</span>
            <div className="flex-1 bg-gray-700 rounded-full h-2">
              <div 
                className="h-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500"
                style={{ width: `${vehicle.completeness || 0}%` }}
              />
            </div>
            <span className="text-sm font-medium text-white">{vehicle.completeness || 0}%</span>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-4 py-2 bg-white/5 border-b border-white/10">
            <TabsList className="flex flex-wrap gap-1 h-auto bg-transparent">
              <TabsTrigger value="overview" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">Übersicht</TabsTrigger>
              <TabsTrigger value="specs" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Spezifikationen {hasData('specifications') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              {hasTuningAccess && (
                <TabsTrigger value="tuning" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                  Tuning {hasData('tuning') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
                </TabsTrigger>
              )}
              <TabsTrigger value="diagnostics" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Diagnose {(hasData('advanced_data.dtc_library') || vehicle.advanced_data?.dtc_library) && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="service" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Service {(hasData('advanced_data.service_intervals') || vehicle.advanced_data?.service_intervals) && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="electrical" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Elektrik {(hasData('advanced_data.network_topology') || vehicle.advanced_data?.network_topology) && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="parts" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Teilenummern {(hasData('advanced_data.part_numbers') || vehicle.advanced_data?.part_numbers) && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="wheels" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Räder/TPMS {(hasData('advanced_data.wheel_tire_fitment') || vehicle.advanced_data?.wheel_tire_fitment) && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="failures" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Schwachstellen {(hasData('advanced_data.failure_modes') || vehicle.advanced_data?.failure_modes) && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="ac" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Klimaanlage {hasData('air_conditioning') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="torque" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Drehmomente {hasData('torque_specs') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              {hasRepairAccess && (
                <TabsTrigger value="repairs" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                  Reparaturen {hasData('repair_guides') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
                </TabsTrigger>
              )}
              <TabsTrigger value="locations" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Positionen {hasData('component_locations') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="fuses" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Sicherungen {hasData('fuses_relays') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="ecus" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Steuergeräte {hasData('ecu_locations') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
              <TabsTrigger value="battery" className="text-xs data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                Batterie {hasData('battery') && <CheckCircle className="w-3 h-3 ml-1 text-green-400" />}
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-0">
              <OverviewTab vehicle={vehicle} onTabChange={setActiveTab} />
            </TabsContent>

            {/* Specifications Tab */}
            <TabsContent value="specs" className="mt-0">
              <SpecsTab specs={displayVehicle.specifications} />
            </TabsContent>

            {/* Tuning Tab - Feature-gated */}
            {hasTuningAccess && (
              <TabsContent value="tuning" className="mt-0">
                <TuningTab tuning={displayVehicle.tuning} />
              </TabsContent>
            )}

            {/* Diagnose Tab - NEU */}
            <TabsContent value="diagnostics" className="mt-0">
              <DiagnosticsTab data={displayVehicle.advanced_data} />
            </TabsContent>

            {/* Service Tab - NEU */}
            <TabsContent value="service" className="mt-0">
              <ServiceTab data={displayVehicle.advanced_data} />
            </TabsContent>

            {/* Elektrik Tab - NEU */}
            <TabsContent value="electrical" className="mt-0">
              <ElectricalTab data={displayVehicle.advanced_data} />
            </TabsContent>

            {/* Teilenummern Tab - NEU */}
            <TabsContent value="parts" className="mt-0">
              <PartsTab data={displayVehicle.advanced_data} />
            </TabsContent>

            {/* Räder/TPMS Tab - NEU */}
            <TabsContent value="wheels" className="mt-0">
              <WheelsTab data={displayVehicle.advanced_data} />
            </TabsContent>

            {/* Schwachstellen Tab - NEU */}
            <TabsContent value="failures" className="mt-0">
              <FailuresTab data={displayVehicle.advanced_data} />
            </TabsContent>

            {/* Air Conditioning Tab */}
            <TabsContent value="ac" className="mt-0">
              <ACTab data={displayVehicle.air_conditioning} />
            </TabsContent>

            {/* Torque Tab */}
            <TabsContent value="torque" className="mt-0">
              <TorqueTab specs={displayVehicle.torque_specs} />
            </TabsContent>

            {/* Repairs Tab - Feature-gated */}
            {hasRepairAccess && (
              <TabsContent value="repairs" className="mt-0">
                <RepairsTab guides={displayVehicle.repair_guides} vehicleId={displayVehicle.id} brand={displayVehicle.brand} model={displayVehicle.model} videos={repairVideos} />
              </TabsContent>
            )}

            {/* Component Locations Tab */}
            <TabsContent value="locations" className="mt-0">
              <LocationsTab locations={displayVehicle.component_locations} />
            </TabsContent>

            {/* Fuses Tab */}
            <TabsContent value="fuses" className="mt-0">
              <FusesTab fuses={displayVehicle.fuses_relays} />
            </TabsContent>

            {/* ECUs Tab */}
            <TabsContent value="ecus" className="mt-0">
              <ECUsTab ecus={displayVehicle.ecu_locations} />
            </TabsContent>

            {/* Battery Tab */}
            <TabsContent value="battery" className="mt-0">
              <BatteryTab battery={displayVehicle.battery} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

// ============================================
// TAB COMPONENTS
// ============================================

const OverviewTab = ({ vehicle, onTabChange }) => {
  // Mapping von Kachel zu Tab-Name
  const cardData = [
    { title: "Bild", icon: Image, has: !!vehicle.image?.path, tab: null },
    { title: "Spezifikationen", icon: Settings, has: !!vehicle.specifications, tab: "specs" },
    { title: "Tuning", icon: Zap, has: !!vehicle.tuning, tab: "tuning" },
    { title: "Diagnose/DTC", icon: Activity, has: !!vehicle.advanced_data?.dtc_library, tab: "diagnostics" },
    { title: "Service", icon: Clock, has: !!vehicle.advanced_data?.service_intervals, tab: "service" },
    { title: "Elektrik", icon: Cable, has: !!vehicle.advanced_data?.network_topology, tab: "electrical" },
    { title: "Teilenummern", icon: Package, has: !!vehicle.advanced_data?.part_numbers, tab: "parts" },
    { title: "Räder/TPMS", icon: Circle, has: !!vehicle.advanced_data?.wheel_tire_fitment, tab: "wheels" },
    { title: "Schwachstellen", icon: AlertTriangle, has: !!vehicle.advanced_data?.failure_modes, tab: "failures" },
    { title: "Klimaanlage", icon: Thermometer, has: !!vehicle.air_conditioning, tab: "ac" },
    { title: "Drehmomente", icon: Wrench, has: !!vehicle.torque_specs, tab: "torque" },
    { title: "Reparaturen", icon: Tool, has: !!vehicle.repair_guides, tab: "repairs" },
    { title: "Positionen", icon: MapPin, has: !!vehicle.component_locations, tab: "locations" },
    { title: "Sicherungen", icon: Shield, has: !!vehicle.fuses_relays, tab: "fuses" },
    { title: "Steuergeräte", icon: Cpu, has: !!vehicle.ecu_locations, tab: "ecus" },
    { title: "Batterie", icon: Battery, has: !!vehicle.battery, tab: "battery" },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cardData.map((card) => (
        <DataCard 
          key={card.title}
          title={card.title} 
          icon={card.icon} 
          has={card.has}
          onClick={card.has && card.tab ? () => onTabChange(card.tab) : null}
        />
      ))}
    </div>
  );
};

const DataCard = ({ title, icon: Icon, has, onClick }) => (
  <div 
    className={`p-4 rounded-lg border transition-all ${
      has 
        ? 'bg-green-900/20 border-green-500/30 hover:bg-green-900/30 hover:border-green-400/50' 
        : 'bg-red-900/20 border-red-500/30'
    } ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
  >
    <div className="flex items-center gap-2">
      <Icon className={`w-5 h-5 ${has ? 'text-green-400' : 'text-red-400'}`} />
      <span className="text-white font-medium">{title}</span>
    </div>
    <p className={`text-sm mt-1 ${has ? 'text-green-400' : 'text-red-400'}`}>
      {has ? 'Vorhanden' : 'Fehlt'}
    </p>
    {onClick && has && (
      <p className="text-xs text-gray-500 mt-1">Klicken für Details →</p>
    )}
  </div>
);

// Mapping für deutsche Feldnamen
const fieldLabels = {
  // Kategorien
  motor: 'Motor', engine: 'Motor',
  kraftstoff: 'Kraftstoff', fuel_system: 'Kraftstoff',
  getriebe: 'Getriebe', transmission: 'Getriebe',
  bremsen: 'Bremsen', brakes: 'Bremsen',
  fahrwerk: 'Fahrwerk', suspension: 'Fahrwerk',
  abmessungen: 'Abmessungen', dimensions: 'Abmessungen',
  service_intervalle: 'Service-Intervalle', service_intervals: 'Service-Intervalle',
  // Motor-Felder
  hubraum_ccm: 'Hubraum (ccm)', displacement_ccm: 'Hubraum (ccm)',
  leistung_ps: 'Leistung (PS)', power_ps: 'Leistung (PS)',
  leistung_kw: 'Leistung (kW)', power_kw: 'Leistung (kW)',
  drehmoment_nm: 'Drehmoment (Nm)', torque_nm: 'Drehmoment (Nm)',
  zylinder: 'Zylinder', cylinder_count: 'Zylinder',
  aufladung: 'Aufladung', turbo: 'Turbo',
  oelkapazitaet_l: 'Ölkapazität (L)', oil_capacity_l: 'Ölkapazität (L)',
  oelspezifikation: 'Ölspezifikation', oil_spec: 'Ölspezifikation',
  kuehlmittel_l: 'Kühlmittel (L)', coolant_capacity_l: 'Kühlmittel (L)',
  // Kraftstoff-Felder
  verbrauch_kombiniert_l: 'Verbrauch kombiniert (L/100km)', consumption_combined_l: 'Verbrauch kombiniert',
  verbrauch_stadt_l: 'Verbrauch Stadt (L/100km)', consumption_city_l: 'Verbrauch Stadt',
  verbrauch_autobahn_l: 'Verbrauch Autobahn (L/100km)', consumption_highway_l: 'Verbrauch Autobahn',
  co2_g_km: 'CO₂ (g/km)',
  tankinhalt_l: 'Tankinhalt (L)', tank_capacity_l: 'Tankinhalt (L)',
  kraftstoffart: 'Kraftstoffart', fuel_type: 'Kraftstoffart',
  einspritzung: 'Einspritzung', injection_system: 'Einspritzung',
  // Getriebe
  typ: 'Typ', type: 'Typ',
  gaenge: 'Gänge', gears: 'Gänge',
  // Bremsen
  scheibe_vorne_mm: 'Scheibe vorne (mm)', front_disc_mm: 'Scheibe vorne (mm)',
  scheibe_hinten_mm: 'Scheibe hinten (mm)', rear_disc_mm: 'Scheibe hinten (mm)',
  fluessigkeit: 'Bremsflüssigkeit', fluid_type: 'Bremsflüssigkeit',
  // Abmessungen
  laenge_mm: 'Länge (mm)', length_mm: 'Länge (mm)',
  breite_mm: 'Breite (mm)', width_mm: 'Breite (mm)',
  hoehe_mm: 'Höhe (mm)', height_mm: 'Höhe (mm)',
  radstand_mm: 'Radstand (mm)', wheelbase_mm: 'Radstand (mm)',
  leergewicht_kg: 'Leergewicht (kg)', weight_kg: 'Gewicht (kg)',
  zuladung_kg: 'Zuladung (kg)', max_load_kg: 'Zuladung (kg)',
  // Service
  oelwechsel_km: 'Ölwechsel (km)', oil_change_km: 'Ölwechsel (km)',
  oelwechsel_monate: 'Ölwechsel (Monate)', oil_change_months: 'Ölwechsel (Monate)',
  inspektion_km: 'Inspektion (km)', inspection_km: 'Inspektion (km)',
  bremsfluessigkeit_jahre: 'Bremsflüssigkeit (Jahre)', brake_fluid_years: 'Bremsflüssigkeit (Jahre)',
  zahnriemen_km: 'Zahnriemen (km)', timing_belt_km: 'Zahnriemen (km)',
  zuendkerzen_km: 'Zündkerzen (km)', spark_plugs_km: 'Zündkerzen (km)'
};

const getLabel = (key) => fieldLabels[key] || key.replace(/_/g, ' ');

const SpecsTab = ({ specs }) => {
  if (!specs) return <EmptyState text="Keine Spezifikationen vorhanden" />;
  
  return (
    <div className="space-y-4">
      {Object.entries(specs).map(([key, value]) => (
        <div key={key} className="bg-white/5 rounded-lg p-4">
          <h4 className="text-cyan-400 font-medium mb-2">{getLabel(key)}</h4>
          {typeof value === 'object' && value !== null ? (
            <div className="grid grid-cols-2 gap-2 text-sm">
              {Object.entries(value).map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-gray-400">{getLabel(k)}:</span>
                  <span className="text-white font-medium">
                    {v === true ? 'Ja' : v === false ? 'Nein' : (v === null || v === 0) ? '-' : String(v)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white">{String(value)}</p>
          )}
        </div>
      ))}
    </div>
  );
};

const TuningTab = ({ tuning }) => {
  if (!tuning) return <EmptyState text="Keine Tuning-Daten vorhanden" />;
  
  const ecuInfo = tuning.ecu_info || tuning.ecu;
  const readMethods = tuning.read_methods || [];
  const tuningTools = tuning.tuning_tools || [];
  const hardwareMethods = tuning.hardware_methods || [];
  const prices = tuning.estimated_price_eur || {};
  const powerCurve = tuning.power_curve;
  const tuningOptions = tuning.options || [];
  
  return (
    <div className="space-y-4">
      {/* Power comparison */}
      <div className="grid grid-cols-3 gap-4">
        <PowerCard title="Original" data={tuning.original} color="gray" />
        <PowerCard 
          title="Stage 1" 
          data={tuning.stage1} 
          color="green" 
          price={tuning.stage1?.price_avg || prices.stage1} 
        />
        <PowerCard 
          title="Stage 2" 
          data={tuning.stage2} 
          color="amber" 
          price={tuning.stage2?.price_avg || prices.stage2}
          hardware={tuning.stage2?.recommended_hardware}
        />
      </div>
      
      {/* ECU Information - Verbesserte Darstellung */}
      {ecuInfo && (
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
          <h4 className="text-blue-400 font-medium mb-3 flex items-center gap-2">
            <Cpu className="w-5 h-5" />
            ECU Information
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoCard label="Hersteller" value={ecuInfo.hersteller || ecuInfo.manufacturer} />
            <InfoCard label="ECU Typ" value={ecuInfo.typ || ecuInfo.model || ecuInfo.type} />
            <InfoCard label="Generation" value={ecuInfo.generation} />
            <InfoCard label="Software" value={ecuInfo.software_version || ecuInfo.software} />
          </div>
        </div>
      )}
      
      {/* Hardware & Lesemethoden - Kompakte Badge-Darstellung */}
      {hardwareMethods.length > 0 && (
        <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
          <h4 className="text-purple-400 font-medium mb-3 flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            Tuning Hardware
            <span className="text-xs text-gray-400 font-normal ml-2">
              ({[...new Set(hardwareMethods.map(h => h.tool))].length} Tools)
            </span>
          </h4>
          
          {/* Gruppiert nach Tool */}
          <div className="space-y-2">
            {(() => {
              // Gruppiere nach Tool
              const toolGroups = {};
              hardwareMethods.forEach(hw => {
                if (!toolGroups[hw.tool]) {
                  toolGroups[hw.tool] = {
                    manufacturer: hw.manufacturer,
                    methods: [],
                    notes: hw.notes,
                    ecu_type: hw.ecu_type
                  };
                }
                toolGroups[hw.tool].methods.push({
                  method: hw.method,
                  read: hw.read,
                  write: hw.write
                });
              });
              
              return Object.entries(toolGroups).map(([toolName, data], idx) => (
                <div 
                  key={idx}
                  className="flex flex-wrap items-center gap-2 p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                >
                  {/* Tool Name */}
                  <div className="flex items-center gap-2 min-w-[180px]">
                    <Wrench className="w-4 h-4 text-purple-400 flex-shrink-0" />
                    <span className="text-white font-medium text-sm">{toolName}</span>
                    {data.manufacturer && data.manufacturer !== toolName && (
                      <span className="text-gray-500 text-xs">({data.manufacturer})</span>
                    )}
                  </div>
                  
                  {/* Methoden-Badges */}
                  <div className="flex flex-wrap gap-1">
                    {data.methods.map((m, mi) => {
                      const canRead = m.read;
                      const canWrite = m.write;
                      const bothOk = canRead && canWrite;
                      const readOnly = canRead && !canWrite;
                      
                      return (
                        <span 
                          key={mi}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                            bothOk ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                            readOnly ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                            'bg-red-500/20 text-red-400 border border-red-500/30'
                          }`}
                          title={`${m.method}: ${canRead ? 'Lesen ✓' : 'Lesen ✗'} | ${canWrite ? 'Schreiben ✓' : 'Schreiben ✗'}`}
                        >
                          {m.method}
                          {bothOk && <span className="text-[10px]">R/W</span>}
                          {readOnly && <span className="text-[10px]">R</span>}
                        </span>
                      );
                    })}
                  </div>
                  
                  {/* Notes (nur wenn vorhanden und kurz) */}
                  {data.notes && data.notes.length < 60 && (
                    <span className="text-gray-500 text-xs ml-auto hidden lg:inline">
                      {data.notes}
                    </span>
                  )}
                </div>
              ));
            })()}
          </div>
          
          {/* Legende */}
          <div className="mt-3 pt-2 border-t border-purple-500/20 flex flex-wrap gap-3 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-400"></span>
              <span className="text-gray-400">R/W = Lesen + Schreiben</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-400"></span>
              <span className="text-gray-400">R = Nur Lesen</span>
            </span>
          </div>
        </div>
      )}
      
      {/* Legacy Lesemethoden & Tools */}
      {(readMethods.length > 0 || tuningTools.length > 0) && hardwareMethods.length === 0 && (
        <div className="grid grid-cols-2 gap-4">
          {readMethods.length > 0 && (
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
              <h4 className="text-green-400 font-medium mb-2">Lesemethoden</h4>
              <div className="flex flex-wrap gap-2">
                {readMethods.map((method, i) => (
                  <span key={i} className="px-2 py-1 bg-green-500/20 rounded text-xs text-green-300">
                    {method}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {tuningTools.length > 0 && (
            <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
              <h4 className="text-purple-400 font-medium mb-2">Kompatible Tools</h4>
              <div className="flex flex-wrap gap-2">
                {tuningTools.map((tool, i) => (
                  <span key={i} className="px-2 py-1 bg-purple-500/20 rounded text-xs text-purple-300">
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Tuning Optionen - NEU */}
      {tuningOptions.length > 0 && (
        <div className="bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-4">
          <h4 className="text-cyan-400 font-medium mb-3 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Tuning-Optionen & Preise
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {tuningOptions.filter(opt => opt.available).map((opt, i) => (
              <div key={i} className="bg-cyan-500/10 rounded p-3">
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm font-medium">{opt.name}</span>
                  {opt.price_avg && (
                    <span className="text-green-400 text-xs font-bold">~{opt.price_avg}€</span>
                  )}
                </div>
                <p className="text-gray-400 text-xs mt-1">{opt.description}</p>
                <span className={`text-xs px-1.5 py-0.5 rounded mt-1 inline-block ${
                  opt.category === 'performance' ? 'bg-orange-500/20 text-orange-300' :
                  opt.category === 'emission' ? 'bg-red-500/20 text-red-300' :
                  opt.category === 'sound' ? 'bg-purple-500/20 text-purple-300' :
                  'bg-blue-500/20 text-blue-300'
                }`}>
                  {opt.category}
                </span>
              </div>
            ))}
          </div>
          {tuningOptions.filter(opt => !opt.available).length > 0 && (
            <p className="text-gray-500 text-xs mt-3">
              Nicht verfügbar für dieses Fahrzeug: {tuningOptions.filter(opt => !opt.available).map(o => o.name).join(', ')}
            </p>
          )}
        </div>
      )}
      
      {/* Leistungsdiagramm Hinweis - NEU */}
      {powerCurve && powerCurve.rpm && (
        <div className="bg-gradient-to-r from-green-900/20 to-amber-900/20 border border-green-500/30 rounded-lg p-4">
          <h4 className="text-green-400 font-medium mb-2 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Leistungsdiagramm-Daten verfügbar
          </h4>
          <p className="text-gray-400 text-sm">
            Drehzahlbereich: {powerCurve.rpm[0]} - {powerCurve.rpm[powerCurve.rpm.length - 1]} U/min
          </p>
          <div className="grid grid-cols-3 gap-4 mt-2 text-xs">
            <div>
              <span className="text-gray-500">Original Peak:</span>
              <span className="text-white ml-2">{Math.max(...(powerCurve.original_hp || []))} PS</span>
            </div>
            <div>
              <span className="text-green-500">Stage 1 Peak:</span>
              <span className="text-white ml-2">{Math.max(...(powerCurve.stage1_hp || []))} PS</span>
            </div>
            <div>
              <span className="text-amber-500">Stage 2 Peak:</span>
              <span className="text-white ml-2">{Math.max(...(powerCurve.stage2_hp || []))} PS</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Tuning Notes */}
      {tuning.tuning_notes && (
        <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4">
          <h4 className="text-amber-400 font-medium mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Hinweise
          </h4>
          <p className="text-sm text-gray-300">{tuning.tuning_notes}</p>
        </div>
      )}
    </div>
  );
};

const PowerCard = ({ title, data, color, price, hardware }) => (
  <div className={`bg-${color}-900/20 border border-${color}-500/30 rounded-lg p-4 text-center`}>
    <h4 className={`text-${color}-400 font-medium`}>{title}</h4>
    <p className="text-2xl font-bold text-white mt-2">
      {data?.power_ps || data?.power || '-'} PS
    </p>
    <p className="text-sm text-gray-400">
      {data?.torque_nm || data?.torque || '-'} Nm
    </p>
    {data?.gains_percent && (
      <p className="text-xs text-green-400 mt-1">
        +{data.gains_percent}% Leistung
      </p>
    )}
    {price && (
      <p className="text-xs text-green-400 mt-2 border-t border-white/10 pt-2">
        Ø {price} €
      </p>
    )}
    {hardware && hardware.length > 0 && (
      <div className="mt-2 border-t border-white/10 pt-2">
        <p className="text-xs text-amber-400 mb-1">Benötigte Hardware:</p>
        <div className="flex flex-wrap gap-1 justify-center">
          {hardware.map((hw, i) => (
            <span key={i} className="text-xs bg-amber-500/20 px-1.5 py-0.5 rounded text-amber-300">
              {hw}
            </span>
          ))}
        </div>
      </div>
    )}
  </div>
);

const ACTab = ({ data }) => {
  if (!data) return <EmptyState text="Keine Klimaanlagen-Daten vorhanden" />;
  
  // Unterstütze deutsche und englische Feldnamen
  const kaeltemittel = data.kaeltemittel_typ || data.refrigerant_type;
  const kaeltemittelMenge = data.kaeltemittel_menge_g || data.refrigerant_amount_g;
  const kompressoroelTyp = data.kompressoroel_typ || data.compressor_oil_type;
  const kompressoroelMenge = data.kompressoroel_menge_ml || data.compressor_oil_amount_ml;
  const kompressorTyp = data.kompressor_typ || data.compressor_type;
  const serviceHinweise = data.service_hinweise || data.service_notes;
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <InfoCard label="Kältemitteltyp" value={kaeltemittel} />
        <InfoCard label="Kältemittelmenge" value={kaeltemittelMenge ? `${kaeltemittelMenge} g` : '-'} />
        <InfoCard label="Kompressoröl Typ" value={kompressoroelTyp} />
        <InfoCard label="Kompressoröl Menge" value={kompressoroelMenge ? `${kompressoroelMenge} ml` : '-'} />
        <InfoCard label="Kompressortyp" value={kompressorTyp} />
      </div>
      
      {serviceHinweise && serviceHinweise.length > 0 && (
        <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4">
          <h4 className="text-amber-400 font-medium mb-2">Service-Hinweise</h4>
          <ul className="list-disc list-inside text-sm text-gray-300">
            {serviceHinweise.map((note, i) => <li key={i}>{note}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

// Drehmoment-Labels
const torqueLabels = {
  radschrauben: 'Radschrauben', wheel_bolts: 'Radschrauben',
  oelablassschraube: 'Ölablassschraube', oil_drain_plug: 'Ölablassschraube',
  zuendkerzen: 'Zündkerzen', spark_plugs: 'Zündkerzen',
  bremssattel: 'Bremssattel', brake_caliper_bolts: 'Bremssattel',
  radlager_mutter: 'Radlagermutter', axle_nut: 'Achsmutter',
  motorlager: 'Motorlager', engine_mounts: 'Motorlager',
  ansaugbruecke: 'Ansaugbrücke', intake_manifold: 'Ansaugbrücke',
  abgaskruemmer: 'Abgaskrümmer', exhaust_manifold: 'Abgaskrümmer',
  zylinderkopf: 'Zylinderkopf', cylinder_head: 'Zylinderkopf',
  drehmoment_nm: 'Drehmoment (Nm)', torque_nm: 'Drehmoment (Nm)',
  anzugsreihenfolge: 'Anzugsreihenfolge', tightening_sequence: 'Anzugsreihenfolge',
  hinweis: 'Hinweis', notes: 'Hinweis',
  vorne_nm: 'Vorne (Nm)', front_nm: 'Vorne (Nm)',
  hinten_nm: 'Hinten (Nm)', rear_nm: 'Hinten (Nm)',
  reihenfolge: 'Reihenfolge', sequence: 'Reihenfolge',
  winkel_grad: 'Winkel (°)', angle_degrees: 'Winkel (°)'
};

const getTorqueLabel = (key) => torqueLabels[key] || key.replace(/_/g, ' ');

const TorqueTab = ({ data }) => {
  if (!data) return <EmptyState text="Keine Anzugsdrehmomente vorhanden" />;
  
  // Helper um verschachtelte Objekte korrekt anzuzeigen
  const renderValue = (v) => {
    if (v === null || v === undefined) return '-';
    if (typeof v === 'number') return `${v} Nm`;
    if (typeof v === 'boolean') return v ? 'Ja' : 'Nein';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') {
      // Verschachteltes Objekt - zeige key:value Paare
      return Object.entries(v).map(([k, val]) => (
        <div key={k} className="ml-2 text-xs">
          <span className="text-gray-500">{getTorqueLabel(k)}: </span>
          <span className="text-white">{renderValue(val)}</span>
        </div>
      ));
    }
    return String(v);
  };
  
  return (
    <div className="space-y-4">
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="bg-white/5 rounded-lg p-4">
          <h4 className="text-cyan-400 font-medium mb-2">
            {getTorqueLabel(key)}
          </h4>
          {typeof value === 'object' && value !== null ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              {Object.entries(value).map(([k, v]) => (
                <div key={k} className="flex flex-col bg-white/5 rounded p-2">
                  <span className="text-gray-400 text-xs mb-1">{getTorqueLabel(k)}</span>
                  <span className="text-white font-mono">
                    {renderValue(v)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white font-mono">{renderValue(value)}</p>
          )}
        </div>
      ))}
    </div>
  );
};

const RepairsTab = ({ guides, vehicleId, brand, model }) => {
  const [expandedGuide, setExpandedGuide] = useState(null);
  const [expandedStep, setExpandedStep] = useState(null);
  const [onDemandVideos, setOnDemandVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [videoError, setVideoError] = useState(null);

  // Automatisch Videos laden beim Mount
  useEffect(() => {
    const loadVideos = async () => {
      if (!vehicleId || !brand || !model) {
        setLoadingVideos(false);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${BACKEND_URL}/api/vehicles/master/${vehicleId}/repair-videos`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: AbortSignal.timeout(12000) // 12 Sekunden Timeout
          }
        );
        const data = await response.json();
        setOnDemandVideos(data.videos || []);
        if (data.error && data.videos?.length === 0) {
          setVideoError(data.error);
        }
      } catch (error) {
        console.error('Failed to load videos:', error);
        setVideoError('Videos konnten nicht geladen werden');
      } finally {
        setLoadingVideos(false);
      }
    };

    loadVideos();
  }, [vehicleId, brand, model]);
  
  // Helper um Werkzeug-Objekte oder Strings zu rendern
  const renderTool = (tool, i) => {
    const toolName = typeof tool === 'object' ? tool.name : tool;
    const toolSize = typeof tool === 'object' ? tool.size : null;
    const toolPurpose = typeof tool === 'object' ? tool.purpose : null;
    
    return (
      <li key={i} className="flex items-start gap-2 p-1.5 bg-white/5 rounded">
        <Wrench className="w-3 h-3 text-cyan-400 mt-0.5 flex-shrink-0" />
        <div>
          <span className="text-white">{toolName}</span>
          {toolSize && <span className="text-gray-400 text-xs ml-1">({toolSize})</span>}
          {toolPurpose && <p className="text-gray-500 text-xs">{toolPurpose}</p>}
        </div>
      </li>
    );
  };

  // Helper um Teil-Objekte oder Strings zu rendern
  const renderPart = (part, i) => {
    const partName = typeof part === 'object' ? part.name : part;
    const partQty = typeof part === 'object' ? part.quantity : null;
    const partOem = typeof part === 'object' ? part.oem_number : null;
    
    return (
      <li key={i} className="flex items-start gap-2 p-1.5 bg-white/5 rounded">
        <Settings className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
        <div>
          <span className="text-white">{partName}</span>
          {partQty && partQty > 1 && <span className="text-gray-400 text-xs ml-1">×{partQty}</span>}
          {partOem && <span className="text-amber-400/70 text-xs ml-1 font-mono">{partOem}</span>}
        </div>
      </li>
    );
  };
  
  return (
    <div className="space-y-4">
      {/* YouTube Videos Section - Automatisch geladen */}
      <div className="bg-gradient-to-r from-red-900/20 to-purple-900/20 rounded-lg border border-red-500/30 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            <span className="text-white font-medium">Reparatur-Videos</span>
            {loadingVideos && (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Lädt...
              </span>
            )}
          </div>
          {!loadingVideos && onDemandVideos.length > 0 && (
            <a
              href={`https://www.youtube.com/results?search_query=${encodeURIComponent(`${brand} ${model} Reparatur Anleitung`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1"
            >
              Mehr auf YouTube →
            </a>
          )}
        </div>

        {/* Loading State */}
        {loadingVideos && (
          <div className="flex items-center justify-center py-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <svg className="w-4 h-4 animate-spin text-red-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              Suche Videos auf YouTube...
            </div>
          </div>
        )}

        {/* Video Grid */}
        {!loadingVideos && onDemandVideos.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {onDemandVideos.slice(0, 4).map((video, idx) => (
              <a
                key={video.id || idx}
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex gap-3 p-2 rounded-lg bg-black/30 hover:bg-black/50 border border-white/5 hover:border-red-500/30 transition-all"
              >
                <div className="relative w-24 h-16 rounded overflow-hidden flex-shrink-0">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/20">
                    <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-white text-xs font-medium line-clamp-2 group-hover:text-red-400">
                    {video.title}
                  </h4>
                  <p className="text-gray-500 text-xs mt-1">{video.channel}</p>
                </div>
              </a>
            ))}
          </div>
        )}

        {/* No Videos Found */}
        {!loadingVideos && onDemandVideos.length === 0 && (
          <div className="text-center py-3">
            <p className="text-gray-400 text-sm">
              {videoError || 'Keine passenden Videos gefunden'}
            </p>
            <a
              href={`https://www.youtube.com/results?search_query=${encodeURIComponent(`${brand} ${model} Reparatur`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 text-sm mt-2"
            >
              <Search className="w-4 h-4" />
              Auf YouTube suchen
            </a>
          </div>
        )}
      </div>

      {/* Repair Guides */}
      {(!guides || guides.length === 0) ? (
        <EmptyState text="Keine Reparaturanleitungen vorhanden" />
      ) : (
        <div className="space-y-3">
          {guides.map((guide, idx) => (
        <div 
          key={guide.id || idx}
          className="bg-white/5 rounded-lg border border-white/10 overflow-hidden"
        >
          {/* Header */}
          <button
            onClick={() => {
              setExpandedGuide(expandedGuide === idx ? null : idx);
              setExpandedStep(null);
            }}
            className="w-full p-4 flex items-center justify-between hover:bg-white/5"
          >
            <div className="flex items-center gap-3">
              <Wrench className="w-5 h-5 text-cyan-400" />
              <div className="text-left">
                <h4 className="text-white font-medium">{guide.title}</h4>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400 mt-1">
                  <span className="px-2 py-0.5 bg-white/10 rounded">{guide.category}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {guide.estimated_time_minutes || guide.time_minutes || '?'} Min
                  </span>
                  <span className={`px-2 py-0.5 rounded ${
                    (guide.difficulty || '').includes('Einfach') ? 'bg-green-500/20 text-green-400' :
                    (guide.difficulty || '').includes('Mittel') ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {guide.difficulty}
                  </span>
                  {guide.steps?.length > 0 && (
                    <span className="text-cyan-400">{guide.steps.length} Schritte</span>
                  )}
                </div>
              </div>
            </div>
            <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${
              expandedGuide === idx ? 'rotate-90' : ''
            }`} />
          </button>
          
          {/* Expanded Content */}
          {expandedGuide === idx && (
            <div className="p-4 border-t border-white/10 space-y-4">
              
              {/* Video-Link mit Sterne-Bewertung */}
              {guide.video ? (
                <div className={`rounded-lg p-3 flex items-center justify-between ${
                  guide.video.found || guide.video.available 
                    ? 'bg-purple-900/20 border border-purple-500/30' 
                    : 'bg-gray-800/50 border border-gray-600/30'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      guide.video.found || guide.video.available ? 'bg-red-600' : 'bg-gray-700'
                    }`}>
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium text-sm">
                          {guide.video.found || guide.video.available ? 'Video-Anleitung' : 'Video-Anleitung'}
                        </span>
                        {(guide.video.found || guide.video.available) && guide.video.quality_rating && (
                          <div className="flex items-center gap-0.5" title={`Quellenqualität: ${guide.video.quality_rating}/5`}>
                            {[...Array(5)].map((_, i) => (
                              <svg 
                                key={i} 
                                className={`w-3 h-3 ${i < guide.video.quality_rating ? 'text-yellow-400' : 'text-gray-600'}`} 
                                fill="currentColor" 
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                              </svg>
                            ))}
                          </div>
                        )}
                      </div>
                      {guide.video.found || guide.video.available ? (
                        <p className="text-gray-400 text-xs mt-0.5">
                          {guide.video.source} • {guide.video.title?.slice(0, 50)}{guide.video.title?.length > 50 ? '...' : ''}
                        </p>
                      ) : (
                        <p className="text-gray-500 text-xs mt-0.5">
                          {guide.video.message || 'Kein passendes Video verfügbar'}
                        </p>
                      )}
                    </div>
                  </div>
                  {(guide.video.found || guide.video.available) && guide.video.url ? (
                    <a
                      href={guide.video.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg flex items-center gap-1.5 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Ansehen
                    </a>
                  ) : (
                    <a
                      href={`https://www.youtube.com/results?search_query=${encodeURIComponent(guide.video.search_suggestion || guide.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg flex items-center gap-1.5 transition-colors"
                    >
                      <Search className="w-4 h-4" />
                      Suchen
                    </a>
                  )}
                </div>
              ) : (
                /* Fallback: Kein Video-Objekt vorhanden - Zeige Suchlink */
                <div className="rounded-lg p-3 flex items-center justify-between bg-gray-800/50 border border-gray-600/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-700">
                      <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                    <div>
                      <span className="text-white font-medium text-sm">Video-Anleitung</span>
                      <p className="text-gray-500 text-xs mt-0.5">
                        Video wird noch gesammelt oder ist nicht verfügbar
                      </p>
                    </div>
                  </div>
                  <a
                    href={`https://www.youtube.com/results?search_query=${encodeURIComponent(guide.title + ' Anleitung')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg flex items-center gap-1.5 transition-colors"
                  >
                    <Search className="w-4 h-4" />
                    Auf YouTube suchen
                  </a>
                </div>
              )}
              
              {/* Safety Warnings - NEU für Haynes-Stil */}
              {guide.safety_warnings && guide.safety_warnings.length > 0 && (
                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
                  <h5 className="text-red-400 font-medium mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Sicherheitshinweise
                  </h5>
                  <div className="space-y-2">
                    {guide.safety_warnings.map((warn, i) => (
                      <div key={i} className="text-sm">
                        <span className={`font-medium ${
                          warn.type === 'danger' ? 'text-red-400' :
                          warn.type === 'warning' ? 'text-amber-400' : 'text-yellow-400'
                        }`}>
                          {warn.title || warn.type?.toUpperCase()}:
                        </span>
                        <span className="text-gray-300 ml-2">{warn.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Tools & Parts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="text-sm text-gray-400 mb-2">Benötigte Werkzeuge</h5>
                  <ul className="text-sm space-y-1 max-h-40 overflow-y-auto">
                    {guide.tools_required?.map(renderTool)}
                  </ul>
                  {guide.special_tools && guide.special_tools.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/10">
                      <h6 className="text-xs text-amber-400 mb-1">Spezialwerkzeuge:</h6>
                      <ul className="text-sm space-y-1">
                        {guide.special_tools.map((tool, i) => renderTool(tool, `special-${i}`))}
                      </ul>
                    </div>
                  )}
                </div>
                <div>
                  <h5 className="text-sm text-gray-400 mb-2">Benötigte Teile</h5>
                  <ul className="text-sm space-y-1 max-h-40 overflow-y-auto">
                    {guide.parts_required?.map(renderPart)}
                  </ul>
                </div>
              </div>
              
              {/* Workspace Requirements - NEU */}
              {guide.workspace_requirements && (
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                  <h5 className="text-blue-400 font-medium mb-2">Arbeitsplatz-Anforderungen</h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    {guide.workspace_requirements.location && (
                      <div><span className="text-gray-400">Ort:</span> <span className="text-white">{guide.workspace_requirements.location}</span></div>
                    )}
                    {guide.workspace_requirements.lighting && (
                      <div><span className="text-gray-400">Licht:</span> <span className="text-white">{guide.workspace_requirements.lighting}</span></div>
                    )}
                    {guide.workspace_requirements.ventilation && (
                      <div><span className="text-gray-400">Belüftung:</span> <span className="text-white">{guide.workspace_requirements.ventilation}</span></div>
                    )}
                    {guide.workspace_requirements.space_needed && (
                      <div><span className="text-gray-400">Platz:</span> <span className="text-white">{guide.workspace_requirements.space_needed}</span></div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Steps - VERBESSERT für detaillierte Haynes-Stil Anleitungen */}
              <div>
                <h5 className="text-sm text-gray-400 mb-3">
                  Arbeitsschritte ({guide.steps?.length || 0})
                </h5>
                <div className="space-y-2">
                  {guide.steps?.map((step, i) => {
                    const isExpanded = expandedStep === i;
                    // Unterstütze beide Feldnamen
                    const instruction = step.detailed_instruction || step.instruction || step.description || '';
                    const phase = step.phase || step.type;
                    
                    return (
                      <div 
                        key={i}
                        className={`rounded-lg border overflow-hidden ${
                          phase === 'testing' || phase === 'verification' ? 'bg-green-900/20 border-green-500/30' :
                          phase === 'preparation' ? 'bg-blue-900/20 border-blue-500/30' :
                          phase === 'reassembly' ? 'bg-purple-900/20 border-purple-500/30' :
                          phase === 'disassembly' ? 'bg-amber-900/20 border-amber-500/30' :
                          'bg-white/5 border-white/10'
                        }`}
                      >
                        {/* Step Header - Klickbar */}
                        <button
                          onClick={() => setExpandedStep(isExpanded ? null : i)}
                          className="w-full p-3 flex items-start gap-3 text-left hover:bg-white/5"
                        >
                          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-cyan-500/20 text-cyan-400 text-sm font-bold flex-shrink-0">
                            {step.step_number || i + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h6 className="text-white font-medium">{step.title || `Schritt ${i + 1}`}</h6>
                              {phase && (
                                <span className="text-xs px-1.5 py-0.5 bg-white/10 rounded text-gray-400 capitalize">
                                  {phase}
                                </span>
                              )}
                              {step.time_minutes && (
                                <span className="text-xs text-gray-500">~{step.time_minutes} Min</span>
                              )}
                            </div>
                            {!isExpanded && instruction && (
                              <p className="text-sm text-gray-400 mt-1 truncate">
                                {instruction.slice(0, 100)}...
                              </p>
                            )}
                          </div>
                          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${
                            isExpanded ? 'rotate-90' : ''
                          }`} />
                        </button>
                        
                        {/* Step Details - Expandiert */}
                        {isExpanded && (
                          <div className="p-3 pt-0 space-y-3 border-t border-white/10">
                            {/* Location Description */}
                            {step.location_description && (
                              <div className="text-sm">
                                <span className="text-cyan-400 font-medium">📍 Position:</span>
                                <p className="text-gray-300 mt-1">{step.location_description}</p>
                              </div>
                            )}
                            
                            {/* Component Description */}
                            {step.component_description && (
                              <div className="text-sm">
                                <span className="text-cyan-400 font-medium">🔧 Komponente:</span>
                                <p className="text-gray-300 mt-1">{step.component_description}</p>
                              </div>
                            )}
                            
                            {/* Main Instruction */}
                            {instruction && (
                              <div className="text-sm">
                                <span className="text-cyan-400 font-medium">📝 Anleitung:</span>
                                <p className="text-gray-200 mt-1 whitespace-pre-line">{instruction}</p>
                              </div>
                            )}
                            
                            {/* Sub-Steps - NEU für Haynes-Stil */}
                            {step.sub_steps && step.sub_steps.length > 0 && (
                              <div className="pl-4 border-l-2 border-cyan-500/30 space-y-2">
                                <span className="text-xs text-gray-400">Teilschritte:</span>
                                {step.sub_steps.map((sub, si) => (
                                  <div key={si} className="text-sm">
                                    <span className="text-cyan-300 font-mono text-xs">
                                      {sub.sub_number || `${step.step_number || i + 1}.${si + 1}`}
                                    </span>
                                    <span className="text-white ml-2">{sub.action}</span>
                                    {sub.detail && (
                                      <p className="text-gray-400 text-xs mt-0.5 ml-6">{sub.detail}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Tool for this step */}
                            {step.tool_for_this_step && (
                              <div className="flex items-center gap-2 text-sm">
                                <Wrench className="w-4 h-4 text-cyan-400" />
                                <span className="text-gray-400">Werkzeug:</span>
                                <span className="text-white">{step.tool_for_this_step}</span>
                              </div>
                            )}
                            
                            {/* Torque */}
                            {step.torque_nm && (
                              <div className="flex items-center gap-2 text-sm bg-cyan-900/30 p-2 rounded">
                                <span className="text-cyan-400 font-mono">⚙️ Drehmoment: {step.torque_nm} Nm</span>
                                {step.torque_sequence && (
                                  <span className="text-gray-400 text-xs">({step.torque_sequence})</span>
                                )}
                              </div>
                            )}
                            
                            {/* Warning */}
                            {step.warning && (
                              <div className="flex items-start gap-2 text-amber-400 text-sm bg-amber-900/20 p-2 rounded">
                                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span>{step.warning}</span>
                              </div>
                            )}
                            
                            {/* Common Mistake */}
                            {step.common_mistake && (
                              <div className="text-sm bg-red-900/20 p-2 rounded">
                                <span className="text-red-400">❌ Häufiger Fehler:</span>
                                <span className="text-gray-300 ml-2">{step.common_mistake}</span>
                              </div>
                            )}
                            
                            {/* Pro Tip */}
                            {step.pro_tip && (
                              <div className="text-sm bg-green-900/20 p-2 rounded">
                                <span className="text-green-400">💡 Profi-Tipp:</span>
                                <span className="text-gray-300 ml-2">{step.pro_tip}</span>
                              </div>
                            )}
                            
                            {/* Visual Indicators */}
                            {step.visual_indicators && (
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                {step.visual_indicators.correct && (
                                  <div className="bg-green-900/20 p-2 rounded">
                                    <span className="text-green-400">✓ Richtig:</span>
                                    <p className="text-gray-300 mt-1">{step.visual_indicators.correct}</p>
                                  </div>
                                )}
                                {step.visual_indicators.incorrect && (
                                  <div className="bg-red-900/20 p-2 rounded">
                                    <span className="text-red-400">✗ Falsch:</span>
                                    <p className="text-gray-300 mt-1">{step.visual_indicators.incorrect}</p>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Expected Result */}
                            {step.expected_result && (
                              <div className="text-sm border-t border-white/10 pt-2">
                                <span className="text-green-400">✓ Erwartetes Ergebnis:</span>
                                <p className="text-gray-300 mt-1">{step.expected_result}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Final Checks - NEU */}
              {guide.final_checks && guide.final_checks.length > 0 && (
                <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3">
                  <h5 className="text-green-400 font-medium mb-2">Abschlussprüfungen</h5>
                  <div className="space-y-2">
                    {guide.final_checks.map((check, i) => (
                      <div key={i} className="text-sm">
                        <span className="text-white">{check.check}</span>
                        {check.expected && <span className="text-gray-400 ml-2">→ {check.expected}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Troubleshooting - NEU */}
              {guide.troubleshooting && guide.troubleshooting.length > 0 && (
                <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-3">
                  <h5 className="text-amber-400 font-medium mb-2">Problemlösung</h5>
                  <div className="space-y-2">
                    {guide.troubleshooting.map((item, i) => (
                      <div key={i} className="text-sm">
                        <span className="text-white font-medium">{item.problem}:</span>
                        <span className="text-gray-300 ml-2">{item.solution}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Tips */}
              {guide.tips && guide.tips.length > 0 && (
                <div className="bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-3">
                  <h5 className="text-cyan-400 font-medium mb-2">Tipps</h5>
                  <ul className="list-disc list-inside text-sm text-gray-300">
                    {guide.tips.map((tip, i) => <li key={i}>{tip}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
        </div>
      )}
    </div>
  );
};

const LocationsTab = ({ data }) => {
  if (!data) return <EmptyState text="Keine Komponenten-Positionen vorhanden" />;
  
  return (
    <div className="space-y-4">
      {Object.entries(data).map(([category, items]) => (
        <div key={category} className="bg-white/5 rounded-lg p-4">
          <h4 className="text-cyan-400 font-medium mb-3 capitalize flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            {category.replace(/_/g, ' ')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {Object.entries(items).map(([name, location]) => (
              <div key={name} className="flex justify-between p-2 bg-white/5 rounded">
                <span className="text-gray-400">{name.replace(/_/g, ' ')}:</span>
                <span className="text-white text-right">{location}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const FusesTab = ({ data }) => {
  if (!data) return <EmptyState text="Keine Sicherungsdaten vorhanden" />;
  
  return (
    <div className="space-y-4">
      {data.fuse_boxes?.map((box, idx) => (
        <div key={idx} className="bg-white/5 rounded-lg p-4">
          <h4 className="text-cyan-400 font-medium mb-2 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            {box.location}
          </h4>
          <p className="text-sm text-gray-400 mb-3">Zugang: {box.access}</p>
          
          {/* Fuses Table */}
          {box.fuses && (
            <div className="mb-4">
              <h5 className="text-sm text-gray-400 mb-2">Sicherungen</h5>
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div className="font-medium text-gray-400">Position</div>
                <div className="font-medium text-gray-400">Ampere</div>
                <div className="font-medium text-gray-400 col-span-2">Funktion</div>
                {box.fuses.map((fuse, i) => (
                  <React.Fragment key={i}>
                    <div className="text-cyan-400 font-mono">{fuse.position}</div>
                    <div className="text-amber-400 font-mono">{fuse.amperage}A</div>
                    <div className="text-white col-span-2">{fuse.function}</div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
          
          {/* Relays */}
          {box.relays && (
            <div>
              <h5 className="text-sm text-gray-400 mb-2">Relais</h5>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {box.relays.map((relay, i) => (
                  <div key={i} className="flex justify-between p-2 bg-white/5 rounded">
                    <span className="text-cyan-400 font-mono">{relay.position}</span>
                    <span className="text-white">{relay.function}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const ECUsTab = ({ data }) => {
  if (!data) return <EmptyState text="Keine Steuergeräte-Informationen vorhanden" />;
  
  return (
    <div className="space-y-3">
      {data.control_units?.map((ecu, idx) => (
        <div key={idx} className="bg-white/5 rounded-lg p-4 border border-white/10">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <Cpu className="w-5 h-5 text-cyan-400" />
              <div>
                <h4 className="text-white font-medium">{ecu.name}</h4>
                <p className="text-sm text-gray-400">{ecu.location}</p>
              </div>
            </div>
            {ecu.obd_address && (
              <span className="text-xs font-mono bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded">
                {ecu.obd_address}
              </span>
            )}
          </div>
          <div className="mt-2 text-sm text-gray-300">
            <span className="text-gray-400">Zugang:</span> {ecu.access}
          </div>
          {ecu.warning && (
            <div className="mt-2 flex items-center gap-2 text-amber-400 text-sm">
              <AlertTriangle className="w-4 h-4" />
              {ecu.warning}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const BatteryTab = ({ data }) => {
  if (!data) return <EmptyState text="Keine Batterie-Informationen vorhanden" />;
  
  const battery = data.main_battery || data;
  
  return (
    <div className="space-y-4">
      {/* Main Battery Info */}
      <div className="bg-white/5 rounded-lg p-4">
        <h4 className="text-cyan-400 font-medium mb-3 flex items-center gap-2">
          <Battery className="w-5 h-5" />
          Hauptbatterie
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard label="Position" value={battery.location} />
          <InfoCard label="Typ" value={battery.type} />
          <InfoCard label="Spannung" value={`${battery.voltage}V`} />
          <InfoCard label="Kapazität" value={`${battery.capacity_ah} Ah`} />
          <InfoCard label="Kälteprüfstrom" value={`${battery.cold_cranking_amps} A`} />
          <InfoCard label="Polart" value={battery.terminal_type} />
        </div>
      </div>
      
      {/* Auxiliary Battery */}
      {data.auxiliary_battery?.present && (
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
          <h4 className="text-blue-400 font-medium mb-2">Zusatzbatterie</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <InfoCard label="Position" value={data.auxiliary_battery.location} small />
            <InfoCard label="Typ" value={data.auxiliary_battery.type} small />
            <InfoCard label="Funktion" value={data.auxiliary_battery.function} small />
          </div>
        </div>
      )}
      
      {/* Coding Info */}
      {data.coding_required && (
        <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4">
          <h4 className="text-amber-400 font-medium mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Codierung erforderlich
          </h4>
          <p className="text-sm text-gray-300">{data.coding_notes}</p>
        </div>
      )}
      
      {/* Procedures */}
      {data.disconnect_procedure && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-cyan-400 font-medium mb-2">Abklemm-Reihenfolge</h4>
          <ol className="list-decimal list-inside text-sm text-gray-300 space-y-1">
            {data.disconnect_procedure.map((step, i) => <li key={i}>{step}</li>)}
          </ol>
        </div>
      )}
    </div>
  );
};

// Helper Components
const InfoCard = ({ label, value, small = false }) => (
  <div className={small ? "" : "bg-white/5 rounded-lg p-3"}>
    <p className="text-gray-400 text-xs">{label}</p>
    <p className={`text-white ${small ? 'text-sm' : 'font-medium'}`}>{value || '-'}</p>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="text-center py-12">
    <AlertCircle className="w-12 h-12 mx-auto text-gray-500 mb-4" />
    <p className="text-gray-400">{text}</p>
    <p className="text-sm text-gray-500 mt-2">Starten Sie die Datensammlung, um diese Informationen zu erfassen.</p>
  </div>
);

// ============================================
// NEUE TAB KOMPONENTEN (Advanced Data)
// ============================================

// Diagnose Tab - DTC, Live Data, Readiness, Adaptations
const DiagnosticsTab = ({ data }) => {
  if (!data) return <EmptyState text="Keine erweiterten Diagnosedaten vorhanden. Starten Sie die erweiterte Datensammlung." />;
  
  const { dtc_library, live_data_reference, readiness_emissions, adaptations_basic_settings } = data;
  
  return (
    <div className="space-y-6">
      {/* DTC Library */}
      {dtc_library?.dtc_codes && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-red-400 font-medium mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Fehlercodes (DTC) - {dtc_library.dtc_codes.length} Codes
          </h4>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {dtc_library.dtc_codes.slice(0, 10).map((dtc, i) => (
              <div key={i} className="bg-white/5 rounded p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-amber-400 font-bold">{dtc.code}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    dtc.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                    dtc.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>{dtc.severity}</span>
                </div>
                <p className="text-white text-sm mb-2">{dtc.description}</p>
                {dtc.common_causes && (
                  <div className="text-xs text-gray-400">
                    <span className="text-gray-500">Häufige Ursachen: </span>
                    {dtc.common_causes.slice(0, 3).map(c => c.cause).join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Live Data Reference */}
      {live_data_reference?.live_data_pids && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-cyan-400 font-medium mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Live-Daten Sollwerte
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(live_data_reference.live_data_pids).map(([key, pid]) => (
              <div key={key} className="bg-white/5 rounded p-3">
                <p className="text-white font-medium text-sm">{pid.name}</p>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-gray-400">Leerlauf:</span>
                  <span className="text-green-400">
                    {pid.idle_value?.min}-{pid.idle_value?.max} {pid.unit}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Adaptations */}
      {adaptations_basic_settings?.adaptations && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-purple-400 font-medium mb-3 flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Anlernprozesse & Grundeinstellungen
          </h4>
          <div className="space-y-3">
            {Object.entries(adaptations_basic_settings.adaptations).slice(0, 6).map(([key, adapt]) => (
              <div key={key} className="bg-white/5 rounded p-3">
                <p className="text-white font-medium text-sm">{adapt.name}</p>
                {adapt.when_required && (
                  <p className="text-xs text-gray-400 mt-1">
                    Erforderlich bei: {adapt.when_required.join(', ')}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Service Tab - Intervals, Fluids
const ServiceTab = ({ data }) => {
  if (!data) return <EmptyState text="Keine Service-Daten vorhanden" />;
  
  const { service_intervals, fluids } = data;
  
  return (
    <div className="space-y-6">
      {/* Service Intervals */}
      {service_intervals?.services && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-green-400 font-medium mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Wartungsintervalle
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(service_intervals.services).map(([key, svc]) => (
              <div key={key} className="bg-white/5 rounded p-3">
                <p className="text-white font-medium text-sm capitalize">{key.replace(/_/g, ' ')}</p>
                <p className="text-cyan-400 text-sm">{svc.interval_km?.toLocaleString()} km</p>
                {svc.items && (
                  <p className="text-xs text-gray-400 mt-1">{svc.items.slice(0, 3).join(', ')}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Fluids */}
      {fluids?.fluids && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-blue-400 font-medium mb-3 flex items-center gap-2">
            <Droplets className="w-4 h-4" />
            Flüssigkeiten & Füllmengen
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(fluids.fluids).map(([key, fluid]) => (
              <div key={key} className="bg-white/5 rounded p-3">
                <p className="text-white font-medium text-sm capitalize">{key.replace(/_/g, ' ')}</p>
                {fluid.capacity_with_filter_l && (
                  <p className="text-cyan-400 text-sm">{fluid.capacity_with_filter_l} L (mit Filter)</p>
                )}
                {fluid.specification && (
                  <p className="text-xs text-gray-400 mt-1">
                    Spez: {Array.isArray(fluid.specification) ? fluid.specification.join(', ') : fluid.specification}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Elektrik Tab - Pinouts, Ground Points, Network
const ElectricalTab = ({ data }) => {
  if (!data) return <EmptyState text="Keine Elektrik-Daten vorhanden" />;
  
  const { connector_pinouts, ground_points, network_topology } = data;
  
  return (
    <div className="space-y-6">
      {/* Network Topology */}
      {network_topology?.network_buses && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-orange-400 font-medium mb-3 flex items-center gap-2">
            <Cable className="w-4 h-4" />
            Netzwerk-Topologie
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(network_topology.network_buses).map(([key, bus]) => (
              <div key={key} className="bg-white/5 rounded p-3">
                <p className="text-white font-medium text-sm">{bus.name || key}</p>
                <p className="text-cyan-400 text-sm">{bus.speed_kbps} kbps</p>
                {bus.participants && (
                  <p className="text-xs text-gray-400 mt-1">
                    {bus.participants.slice(0, 3).join(', ')}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Ground Points */}
      {ground_points?.ground_points && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-yellow-400 font-medium mb-3 flex items-center gap-2">
            <Circle className="w-4 h-4" />
            Massepunkte
          </h4>
          <div className="space-y-3">
            {ground_points.ground_points.map((gp, i) => (
              <div key={i} className="bg-white/5 rounded p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-amber-400">{gp.id}</span>
                  <span className="text-white font-medium text-sm">{gp.name}</span>
                </div>
                <p className="text-xs text-gray-400">{gp.location}</p>
                {gp.symptoms_if_faulty && (
                  <p className="text-xs text-red-400 mt-1">
                    Bei Defekt: {gp.symptoms_if_faulty.slice(0, 2).join(', ')}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Connector Pinouts */}
      {connector_pinouts?.connectors && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-purple-400 font-medium mb-3 flex items-center gap-2">
            <Cpu className="w-4 h-4" />
            Steckerbelegungen
          </h4>
          <div className="space-y-3">
            {Object.entries(connector_pinouts.connectors).slice(0, 4).map(([key, conn]) => (
              <div key={key} className="bg-white/5 rounded p-3">
                <p className="text-white font-medium text-sm">{conn.name}</p>
                <p className="text-xs text-gray-400">{conn.connector_type} - {conn.location}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Parts Tab - Part Numbers, TSBs
const PartsTab = ({ data }) => {
  if (!data) return <EmptyState text="Keine Teilenummern-Daten vorhanden" />;
  
  const { part_numbers, tsb_recalls } = data;
  
  return (
    <div className="space-y-6">
      {/* Part Numbers */}
      {part_numbers?.parts && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-cyan-400 font-medium mb-3 flex items-center gap-2">
            <Package className="w-4 h-4" />
            OE-Teilenummern
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(part_numbers.parts).map(([key, part]) => (
              <div key={key} className="bg-white/5 rounded p-3">
                <p className="text-white font-medium text-sm capitalize">{key.replace(/_/g, ' ')}</p>
                <p className="font-mono text-amber-400 text-sm">{part.oem_number}</p>
                {part.alternatives && part.alternatives.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    Alt: {part.alternatives.slice(0, 2).map(a => `${a.brand} ${a.number}`).join(', ')}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* TSBs & Recalls */}
      {tsb_recalls && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-red-400 font-medium mb-3 flex items-center gap-2">
            <FileWarning className="w-4 h-4" />
            Service Bulletins & Rückrufe
          </h4>
          
          {/* Recalls */}
          {tsb_recalls.recalls?.length > 0 && (
            <div className="mb-4">
              <h5 className="text-sm text-red-400 mb-2">Rückrufe</h5>
              <div className="space-y-2">
                {tsb_recalls.recalls.map((recall, i) => (
                  <div key={i} className="bg-red-900/20 border border-red-500/30 rounded p-2">
                    <p className="text-white text-sm font-medium">{recall.component}</p>
                    <p className="text-xs text-gray-400">{recall.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Known Issues */}
          {tsb_recalls.known_issues?.length > 0 && (
            <div>
              <h5 className="text-sm text-amber-400 mb-2">Bekannte Probleme</h5>
              <div className="space-y-2">
                {tsb_recalls.known_issues.slice(0, 5).map((issue, i) => (
                  <div key={i} className="bg-amber-900/20 border border-amber-500/30 rounded p-2">
                    <p className="text-white text-sm font-medium">{issue.component}</p>
                    <p className="text-xs text-gray-400">{issue.description}</p>
                    {issue.symptoms && (
                      <p className="text-xs text-amber-400 mt-1">Symptome: {issue.symptoms.join(', ')}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Wheels Tab - Fitment, TPMS
const WheelsTab = ({ data }) => {
  if (!data) return <EmptyState text="Keine Rad/Reifen-Daten vorhanden" />;
  
  const { wheel_tire_fitment, tpms } = data;
  
  return (
    <div className="space-y-6">
      {/* Wheel Fitment */}
      {wheel_tire_fitment && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-cyan-400 font-medium mb-3 flex items-center gap-2">
            <Circle className="w-4 h-4" />
            Rad- & Reifendaten
          </h4>
          
          {/* Bolt Pattern */}
          {wheel_tire_fitment.bolt_pattern && (
            <div className="bg-white/5 rounded p-3 mb-3">
              <p className="text-gray-400 text-xs">Lochkreis</p>
              <p className="text-white font-mono text-lg">{wheel_tire_fitment.bolt_pattern.pcd}</p>
              <p className="text-xs text-gray-400">
                Zentrierung: {wheel_tire_fitment.bolt_pattern.center_bore_mm}mm | 
                Gewinde: {wheel_tire_fitment.bolt_pattern.bolt_thread}
              </p>
            </div>
          )}
          
          {/* OEM Sizes */}
          {wheel_tire_fitment.oem_sizes && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {wheel_tire_fitment.oem_sizes.map((size, i) => (
                <div key={i} className="bg-white/5 rounded p-3">
                  <p className="text-amber-400 text-xs">{size.trim_level}</p>
                  <p className="text-white font-medium">{size.wheel_size}</p>
                  <p className="text-cyan-400 text-sm">{size.tire_size}</p>
                  <p className="text-xs text-gray-400">ET {size.offset_et}</p>
                </div>
              ))}
            </div>
          )}
          
          {/* Tire Pressure */}
          {wheel_tire_fitment.tire_pressure && (
            <div className="mt-3 bg-blue-900/20 border border-blue-500/30 rounded p-3">
              <p className="text-blue-400 text-sm font-medium mb-2">Reifendruck</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-400 text-xs">Vorne</p>
                  <p className="text-white">{wheel_tire_fitment.tire_pressure.front_bar?.normal_load} bar</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">Hinten</p>
                  <p className="text-white">{wheel_tire_fitment.tire_pressure.rear_bar?.normal_load} bar</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* TPMS */}
      {tpms && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-green-400 font-medium mb-3 flex items-center gap-2">
            <Gauge className="w-4 h-4" />
            RDKS / Reifendruckkontrolle
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <InfoCard label="Typ" value={tpms.tpms_type} />
            {tpms.direct_tpms?.sensor_frequency_mhz && (
              <InfoCard label="Frequenz" value={`${tpms.direct_tpms.sensor_frequency_mhz} MHz`} />
            )}
          </div>
          {tpms.direct_tpms?.relearn_procedure?.steps && (
            <div className="mt-3 bg-white/5 rounded p-3">
              <p className="text-sm text-gray-400 mb-2">Anlernprozedur:</p>
              <ol className="list-decimal list-inside text-xs text-gray-300 space-y-1">
                {tpms.direct_tpms.relearn_procedure.steps.slice(0, 4).map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Failures Tab - Failure Modes, Tuning Guardrails
const FailuresTab = ({ data }) => {
  // Prüfe ob Daten vorhanden sind
  if (!data) return <EmptyState text="Keine Schwachstellen-Daten vorhanden. Starten Sie das Update um diese zu sammeln." />;
  
  const { failure_modes, safe_tuning_guardrails } = data;
  
  // FIX: failure_modes kann direkt ein Array sein ODER ein Objekt mit failure_modes Array
  const failureModesArray = Array.isArray(failure_modes) 
    ? failure_modes 
    : (failure_modes?.failure_modes || []);
  
  // Prüfe ob mindestens ein Datensatz vorhanden ist
  const hasFailureModes = failureModesArray && failureModesArray.length > 0;
  const hasGuardrails = safe_tuning_guardrails?.engine_limits && Object.keys(safe_tuning_guardrails.engine_limits).length > 0;
  
  if (!hasFailureModes && !hasGuardrails) {
    return <EmptyState text="Schwachstellen-Daten werden noch gesammelt. Bitte warten Sie bis das Update abgeschlossen ist." />;
  }
  
  return (
    <div className="space-y-6">
      {/* Failure Modes */}
      {hasFailureModes && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-red-400 font-medium mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Typische Schwachstellen ({failureModesArray.length})
          </h4>
          <div className="space-y-3">
            {failureModesArray.map((fm, i) => (
              <div key={i} className="bg-red-900/20 border border-red-500/30 rounded p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium">{fm.component || fm.name || 'Unbekannt'}</span>
                  {fm.mileage_typically_fails && (
                    <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                      {fm.mileage_typically_fails.min_km?.toLocaleString() || '?'}-{fm.mileage_typically_fails.max_km?.toLocaleString() || '?'} km
                    </span>
                  )}
                  {fm.affected_years && (
                    <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded ml-2">
                      {fm.affected_years}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-300">{fm.failure_type || fm.description || ''}</p>
                {fm.root_cause && (
                  <p className="text-xs text-gray-400 mt-1">Ursache: {fm.root_cause}</p>
                )}
                {fm.symptoms && Array.isArray(fm.symptoms) && fm.symptoms.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-400">Symptome:</p>
                    <p className="text-xs text-amber-400">{fm.symptoms.join(', ')}</p>
                  </div>
                )}
                {fm.prevention && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-400">Prävention:</p>
                    <p className="text-xs text-green-400">
                      {Array.isArray(fm.prevention) ? fm.prevention.join(', ') : fm.prevention}
                    </p>
                  </div>
                )}
                {fm.repair_cost_eur && (
                  <p className="text-xs text-cyan-400 mt-2">
                    Reparaturkosten: {fm.repair_cost_eur.min || '?'}-{fm.repair_cost_eur.max || '?'} €
                  </p>
                )}
                {fm.ignore_risk && (
                  <p className="text-xs text-red-500 mt-2 font-medium">
                    ⚠️ Risiko bei Ignorieren: {fm.ignore_risk}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Tuning Guardrails */}
      {hasGuardrails && (
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-amber-400 font-medium mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Sichere Tuning-Grenzen
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(safe_tuning_guardrails.engine_limits).map(([key, limit]) => (
              <div key={key} className="bg-white/5 rounded p-3">
                <p className="text-white font-medium text-sm capitalize">{key.replace(/_/g, ' ')}</p>
                {limit.stage1_max_mbar && (
                  <div className="text-xs mt-1">
                    <span className="text-green-400">Stage 1: {limit.stage1_max_mbar}</span>
                    {limit.stage2_max_mbar && (
                      <span className="text-amber-400 ml-2">Stage 2: {limit.stage2_max_mbar}</span>
                    )}
                  </div>
                )}
                {limit.normal_max_c && (
                  <p className="text-xs text-gray-400">
                    Normal: {limit.normal_max_c}°C | Kritisch: {limit.critical_c}°C
                  </p>
                )}
                {limit.warning && (
                  <p className="text-xs text-red-400 mt-1">{limit.warning}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDetailView;
